import { PartnerName } from 'configs/constants';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Partner-specific configs
import haloviewConfig, { getHaloViewConfig } from './haloview/config';
import homiqConfig, { getConfig } from './homiq/config';
import instaviewConfig, { getInstaViewConfig } from './instaview/config';
import lunaConfig, { getLunaConfig } from './luna/config';
import newaieyeConfig, { getNewaieyeConfig } from './newaieye/config';
import waiscenConfig, { getWaiscenConfig } from './waiscen/config';

// Partner-specific themes
import haloviewTheme from './haloview/lightTheme';
import homiqTheme from './homiq/lightTheme';
import instaviewTheme from './instaview/lightTheme';
import lunaTheme from './luna/lightTheme';
import newaieyeTheme from './newaieye/lightTheme';
import waiscenTheme from './waiscen/lightTheme';

import haloviewDarkTheme from './haloview/darkTheme';
import homiqDarkTheme from './homiq/darkTheme';
import instaviewDarkTheme from './instaview/darkTheme';
import lunaDarkTheme from './luna/darkTheme';
import newaieyeDarkTheme from './newaieye/darkTheme';
import waiscenDarkTheme from './waiscen/darkTheme';

const PARTNER = import.meta.env.VITE_PARTNER || 'instaview';

const getPartnerDetails = () => {
  switch (PARTNER) {
    case PartnerName.WAISCEN:
      return {
        config: waiscenConfig,
        name: PartnerName.WAISCEN
      };
    case PartnerName.HALOVUE:
      return {
        config: haloviewConfig,
        name: PartnerName.HALOVUE
      };
    case PartnerName.HOMIQ:
      return {
        config: homiqConfig,
        name: PartnerName.HOMIQ
      };

    case PartnerName.LUNA:
      return {
        config: lunaConfig,

        name: PartnerName.LUNA
      };

    case PartnerName.NEWAIEYE:
      return {
        config: newaieyeConfig,
        name: PartnerName.NEWAIEYE
      };

    // iv_sandbox is an InstaView sandbox tenant: identical config, themes and
    // assets, only the partner id (sent as the Partner-ID header) and the
    // Firebase project differ.
    case PartnerName.IV_SANDBOX:
      return {
        config: instaviewConfig,
        name: PartnerName.IV_SANDBOX
      };

    case PartnerName.INSTAVIEW:
    default:
      return {
        config: instaviewConfig,
        name: PartnerName.INSTAVIEW
      };
  }
};

export const getPartnerTheme = (isDarkMode: boolean) => {
  switch (PARTNER) {
    case PartnerName.WAISCEN:
      return isDarkMode ? waiscenDarkTheme : waiscenTheme;
    case PartnerName.HALOVUE:
      return isDarkMode ? haloviewDarkTheme : haloviewTheme;
    case PartnerName.HOMIQ:
      return isDarkMode ? homiqDarkTheme : homiqTheme;

    case PartnerName.LUNA:
      return isDarkMode ? lunaDarkTheme : lunaTheme;

    case PartnerName.NEWAIEYE:
      return isDarkMode ? newaieyeDarkTheme : newaieyeTheme;

    case PartnerName.IV_SANDBOX:
    case PartnerName.INSTAVIEW:
    default:
      return isDarkMode ? instaviewDarkTheme : instaviewTheme;
  }
};

const { config, name } = getPartnerDetails();
export { config, name };

export const usePartnerConfig = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'partnerConfig'
  });

  const partnerTheme = getPartnerTheme(true);

  const translatedConfig = useMemo(() => {
    switch (name) {
      case PartnerName.HALOVUE:
        return getHaloViewConfig(t);
      case PartnerName.HOMIQ:
        return getConfig(t);
      case PartnerName.LUNA:
        return getLunaConfig(t);
      case PartnerName.NEWAIEYE:
        return getNewaieyeConfig(t);
      case PartnerName.WAISCEN:
        return getWaiscenConfig(t);
      case PartnerName.IV_SANDBOX:
      case PartnerName.INSTAVIEW:
      default:
        return getInstaViewConfig(t);
    }
  }, [t]);

  return {
    config: translatedConfig,
    partnerTheme,
    name
  };
};

export const getPartnerName = (): PartnerName => {
  return name;
};
